'use client';

/**
 * Shown when `/m/<slug>` could not be LOADED — as distinct from a slug that has
 * no menu behind it.
 *
 * WHY THIS IS A SEPARATE PAGE. Both cases used to render "Menu not found —
 * there's no menu at /m/… Check the link for a typo." For a transient database
 * failure that sentence is false in the most damaging possible way: the guest is
 * standing in the restaurant holding their phone over the merchant's printed QR
 * code, and the product tells them the merchant's sticker is wrong. They give
 * up, the merchant never hears why, and the menu was live the whole time.
 * Observed on a menu that was `status:live, is_published:true`.
 *
 * So this page says the true thing — we could not load it — and leads with the
 * only action that can actually work: try again. It deliberately does NOT offer
 * a directory search: the guest does not want a different restaurant, they want
 * this one, and a list of alternatives implies they came to the wrong place.
 */

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2, RefreshCw, WifiOff } from 'lucide-react';

import { Button } from '@/components/ui/button';

export default function MenuUnavailablePage() {
  const router = useRouter();
  const [retrying, setRetrying] = useState(false);

  const retry = () => {
    setRetrying(true);
    // A fresh server render, not a client refetch — the failure was server-side,
    // so re-running the same client state would prove nothing.
    router.refresh();
    // The spinner is a promise we can keep: refresh() resolves nothing we can
    // await, so it is released on a timer rather than left spinning forever.
    window.setTimeout(() => setRetrying(false), 4000);
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center px-6 py-16 text-center">
      <span className="mb-5 inline-flex size-14 items-center justify-center rounded-full bg-[var(--color-bg-sunken)]">
        <WifiOff aria-hidden="true" className="size-6 text-[var(--color-text-secondary)]" />
      </span>

      <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
        This menu didn&rsquo;t load
      </h1>

      <p className="pt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        The link is fine — we just couldn&rsquo;t reach the menu. This is usually
        over in a few seconds.
      </p>

      <Button onClick={retry} disabled={retrying} className="mt-6 w-full max-w-xs gap-2">
        {retrying ? (
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
        ) : (
          <RefreshCw aria-hidden="true" className="size-4" />
        )}
        {retrying ? 'Trying again…' : 'Try again'}
      </Button>

      <p className="pt-4 text-xs text-[var(--color-text-secondary)]">
        Still stuck? Ask a member of staff — they can take your order directly.
      </p>
    </main>
  );
}
